import React, { useRef, useState } from 'react';
import {
  VStack, Input, Select, Textarea, Text, Box, Button, SimpleGrid,
  FormLabel, FormControl, HStack, Badge, Icon, useToast, Image, Divider, Link,
} from '@chakra-ui/react';
import { FiImage, FiUpload } from 'react-icons/fi';
import deportesApi, { Deportista, API_UPLOADS } from '../../../api/deportesApi';
import {
  SectionTitle, Field, useCatalogos, calcularEdad, fmtFecha, nombreCompleto, ROJO, ROJO_HOVER,
} from '../_shared';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: Deportista;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const SEXOS = ['Masculino', 'Femenino', 'Otro'];
const NIVELES = ['Principiante', 'Intermedio', 'Avanzado', 'Alto rendimiento'];

const FormularioDeportista: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const { disciplinas, categorias, localidades } = useCatalogos();
  const fotoRef = useRef<HTMLInputElement>(null);

  const [f, setF] = useState<any>({
    nombre: data?.nombre || '',
    apPaterno: data?.apPaterno || '',
    apMaterno: data?.apMaterno || '',
    fechaNacimiento: data?.fechaNacimiento ? data.fechaNacimiento.split('T')[0] : '',
    sexo: data?.sexo || '',
    curp: data?.curp || '',
    telefono: data?.telefono || '',
    celular: data?.celular || '',
    email: data?.email || '',
    estado: data?.estado || 'Tlaxcala',
    municipio: data?.municipio || 'Totolac',
    localidadId: data?.localidadId ? String(data.localidadId) : '',
    localidad: data?.localidad || '',
    colonia: data?.colonia || '',
    codigoPostal: data?.codigoPostal || '',
    tipoVialidad: data?.tipoVialidad || '',
    vialidad: data?.vialidad || '',
    numeroExterior: data?.numeroExterior || '',
    numeroInterior: data?.numeroInterior || '',
    referencias: data?.referencias || '',
    disciplinaId: data?.disciplinaId ? String(data.disciplinaId) : '',
    categoriaId: data?.categoriaId ? String(data.categoriaId) : '',
    entrenador: data?.entrenador || '',
    nivel: data?.nivel || '',
    institucionEducativa: data?.institucionEducativa || '',
    observaciones: data?.observaciones || '',
    estatus: data?.estatus || 'activo',
  });
  const [foto, setFoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>(data?.fotografia ? `${API_UPLOADS}${data.fotografia}` : '');
  const [guardando, setGuardando] = useState(false);

  const set = (k: string, v: any) => setF((prev: any) => ({ ...prev, [k]: v }));

  const handleFoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Solo se permiten imágenes', status: 'warning', duration: 3000 });
      return;
    }
    setFoto(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleLocalidad = (id: string) => {
    const loc = localidades.find((l: any) => String(l.id) === id);
    set('localidadId', id);
    set('localidad', loc?.descripcion || '');
  };

  const handleGuardar = async () => {
    if (!f.nombre) {
      toast({ title: 'El nombre es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, (v ?? '') as string));
      if (foto) fd.append('fotografia', foto);

      if (modo === 'edit' && data) {
        await deportesApi.actualizarDeportista(data.id, fd);
      } else {
        await deportesApi.crearDeportista(fd);
      }
      toast({ title: modo === 'edit' ? 'Deportista actualizado' : 'Deportista registrado', status: 'success', duration: 3000 });
      onGuardado?.();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  const edad = calcularEdad(f.fechaNacimiento);

  // ────────── MODO VISTA ──────────
  if (modo === 'view') {
    const d = data as Deportista;
    const edadView = d.edad ?? calcularEdad(d.fechaNacimiento);
    return (
      <VStack spacing={3} align="stretch">
        <HStack spacing={4} align="start">
          <Box w="110px" h="110px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200" bg="gray.100" flexShrink={0}>
            {d.fotografia ? (
              <Link href={`${API_UPLOADS}${d.fotografia}`} isExternal title="Ver fotografía en tamaño completo">
                <Image src={`${API_UPLOADS}${d.fotografia}`} alt={d.nombre} w="100%" h="100%" objectFit="cover" cursor="pointer" />
              </Link>
            ) : (
              <Box w="100%" h="100%" display="flex" alignItems="center" justifyContent="center">
                <Icon as={FiImage} boxSize={8} color="gray.400" />
              </Box>
            )}
          </Box>
          <Box flex={1}>
            <Text fontSize="lg" fontWeight="bold" color={ROJO}>{nombreCompleto(d)}</Text>
            <HStack spacing={2} mt={1} flexWrap="wrap">
              <Badge colorScheme={d.estatus === 'activo' ? 'green' : 'gray'}>{d.estatus}</Badge>
              {edadView != null && <Badge colorScheme="blue">{edadView} años</Badge>}
              {d.sexo && <Badge colorScheme="purple">{d.sexo}</Badge>}
            </HStack>
          </Box>
        </HStack>

        <SectionTitle>Datos personales</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <Field label="FECHA DE NACIMIENTO" value={fmtFecha(d.fechaNacimiento)} />
          <Field label="EDAD" value={edadView != null ? `${edadView} años` : undefined} />
          <Field label="CURP" value={d.curp} />
          <Field label="TELÉFONO" value={d.telefono} />
          <Field label="CELULAR" value={d.celular} />
          <Field label="EMAIL" value={d.email} />
        </SimpleGrid>

        <SectionTitle>Domicilio</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <Field label="LOCALIDAD" value={d.localidad} />
          <Field label="COLONIA" value={d.colonia} />
          <Field label="C.P." value={d.codigoPostal} />
          <Field label="VIALIDAD" value={[d.tipoVialidad, d.vialidad].filter(Boolean).join(' ')} />
          <Field label="No. EXT / INT" value={[d.numeroExterior, d.numeroInterior].filter(Boolean).join(' / ')} />
          <Field label="REFERENCIAS" value={d.referencias} />
        </SimpleGrid>

        <SectionTitle>Información deportiva</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
          <Field label="DISCIPLINA" value={d.disciplina?.nombre} />
          <Field label="CATEGORÍA" value={d.categoria?.nombre} />
          <Field label="EQUIPO" value={d.equipo?.nombre} />
          <Field label="ENTRENADOR" value={d.entrenador} />
          <Field label="NIVEL" value={d.nivel} />
          <Field label="INSTITUCIÓN EDUCATIVA" value={d.institucionEducativa} />
        </SimpleGrid>
        {d.observaciones && (
          <>
            <SectionTitle>Observaciones</SectionTitle>
            <Text fontSize="sm" whiteSpace="pre-wrap">{d.observaciones}</Text>
          </>
        )}
      </VStack>
    );
  }

  // ────────── MODO NUEVO / EDITAR ──────────
  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Fotografía</SectionTitle>
      <HStack spacing={4}>
        <Box w="100px" h="100px" borderRadius="md" overflow="hidden" border="1px solid" borderColor="gray.200" bg="gray.100" flexShrink={0}>
          {preview ? (
            <Image src={preview} alt="preview" w="100%" h="100%" objectFit="cover" />
          ) : (
            <Box w="100%" h="100%" display="flex" alignItems="center" justifyContent="center">
              <Icon as={FiImage} boxSize={7} color="gray.400" />
            </Box>
          )}
        </Box>
        <Box>
          <input ref={fotoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFoto} />
          <Button size="sm" leftIcon={<Icon as={FiUpload} />} variant="outline" colorScheme="red" onClick={() => fotoRef.current?.click()}>
            {preview ? 'Cambiar foto' : 'Subir foto'}
          </Button>
          <Text fontSize="xs" color="gray.400" mt={1}>JPG, PNG o WEBP (máx. 10MB)</Text>
        </Box>
      </HStack>

      <SectionTitle>Datos personales</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Nombre(s)</FormLabel>
          <Input value={f.nombre} onChange={(e) => set('nombre', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Apellido paterno</FormLabel>
          <Input value={f.apPaterno} onChange={(e) => set('apPaterno', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Apellido materno</FormLabel>
          <Input value={f.apMaterno} onChange={(e) => set('apMaterno', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Fecha de nacimiento</FormLabel>
          <Input type="date" value={f.fechaNacimiento} onChange={(e) => set('fechaNacimiento', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Edad (calculada)</FormLabel>
          <Input isReadOnly value={edad != null ? `${edad} años` : ''} placeholder="—" bg="gray.50" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Sexo</FormLabel>
          <Select placeholder="Selecciona" value={f.sexo} onChange={(e) => set('sexo', e.target.value)}>
            {SEXOS.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">CURP</FormLabel>
          <Input maxLength={18} value={f.curp} onChange={(e) => set('curp', e.target.value.toUpperCase())} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Teléfono</FormLabel>
          <Input value={f.telefono} onChange={(e) => set('telefono', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Celular</FormLabel>
          <Input value={f.celular} onChange={(e) => set('celular', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Email</FormLabel>
          <Input type="email" value={f.email} onChange={(e) => set('email', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Domicilio</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Localidad</FormLabel>
          <Select placeholder="Selecciona" value={f.localidadId} onChange={(e) => handleLocalidad(e.target.value)}>
            {localidades.map((l: any) => <option key={l.id} value={l.id}>{l.descripcion}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Colonia</FormLabel>
          <Input value={f.colonia} onChange={(e) => set('colonia', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Código postal</FormLabel>
          <Input value={f.codigoPostal} onChange={(e) => set('codigoPostal', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Tipo de vialidad</FormLabel>
          <Input placeholder="Calle, Av., Privada..." value={f.tipoVialidad} onChange={(e) => set('tipoVialidad', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Vialidad</FormLabel>
          <Input value={f.vialidad} onChange={(e) => set('vialidad', e.target.value)} />
        </FormControl>
        <HStack>
          <FormControl>
            <FormLabel fontSize="sm">No. Ext.</FormLabel>
            <Input value={f.numeroExterior} onChange={(e) => set('numeroExterior', e.target.value)} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">No. Int.</FormLabel>
            <Input value={f.numeroInterior} onChange={(e) => set('numeroInterior', e.target.value)} />
          </FormControl>
        </HStack>
        <FormControl gridColumn={{ md: 'span 3' }}>
          <FormLabel fontSize="sm">Referencias</FormLabel>
          <Input value={f.referencias} onChange={(e) => set('referencias', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Información deportiva</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Disciplina</FormLabel>
          <Select placeholder="Selecciona" value={f.disciplinaId} onChange={(e) => set('disciplinaId', e.target.value)}>
            {disciplinas.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Categoría</FormLabel>
          <Select placeholder="Selecciona" value={f.categoriaId} onChange={(e) => set('categoriaId', e.target.value)}>
            {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Nivel</FormLabel>
          <Select placeholder="Selecciona" value={f.nivel} onChange={(e) => set('nivel', e.target.value)}>
            {NIVELES.map((n) => <option key={n} value={n}>{n}</option>)}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Entrenador</FormLabel>
          <Input value={f.entrenador} onChange={(e) => set('entrenador', e.target.value)} />
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Institución educativa</FormLabel>
          <Input value={f.institucionEducativa} onChange={(e) => set('institucionEducativa', e.target.value)} />
        </FormControl>
        {modo === 'edit' && (
          <FormControl>
            <FormLabel fontSize="sm">Estatus</FormLabel>
            <Select value={f.estatus} onChange={(e) => set('estatus', e.target.value)}>
              <option value="activo">activo</option>
              <option value="inactivo">inactivo</option>
            </Select>
          </FormControl>
        )}
        <FormControl gridColumn={{ md: 'span 3' }}>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={2} value={f.observaciones} onChange={(e) => set('observaciones', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <Divider />
      <HStack justify="flex-end" pt={1} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={ROJO} color="white" _hover={{ bg: ROJO_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioDeportista;
